import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { FileFormFieldError } from '@/components/molecules/FileFormFieldError';
import { useTranslator } from '@/hooks/useTranslator';
import { formatFileSize } from '@/utility/formatFileSize';

interface Props {
  readonly name: string;
  readonly size: number | null;

  readonly icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  readonly status?: QueryStatus;
  readonly error?: string | null;

  onIconClick?(): void;
}

export const DocumentPreviewWidget: React.FC<Props> = (props: Props): React.ReactElement => {
  const { error, name, size, status, icon, onIconClick } = props;

  const tr = useTranslator();

  const Icon = React.useCallback(
    function Icon(): React.ReactElement | null {
      const IconComponent = icon;

      switch (status) {
        case QueryStatus.pending:
          return (
            <Box display="flex" justifyContent="center" alignItems="center" width={36} height={36}>
              <CircularProgress size={22} thickness={7} />
            </Box>
          );
        case QueryStatus.rejected:
          return <FileFormFieldError error={error} onDelete={onIconClick} />;
        default:
          if (onIconClick) {
            return (
              <IconButton color="inherit" onClick={onIconClick}>
                <IconComponent fill="currentColor" />
              </IconButton>
            );
          } else {
            return null;
          }
      }
    },
    [error, icon, onIconClick, status],
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.textContainer}>
        <Typography
          sx={styles.emptyText}
          variant="subtitle1"
          title={tr('DOCUMENT_WITH_NAME_TEMPLATE', name)}>
          {tr('DOCUMENT_WITH_NAME_TEMPLATE', name)}
        </Typography>
        <Typography sx={styles.emptyText} variant="subtitle1" color="custom.LightGray">
          {size !== null ? formatFileSize(size) : ''}
        </Typography>
      </Box>
      <Box sx={styles.removeButton}>
        <Icon />
      </Box>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'custom.OffWhite',
    borderColor: 'custom.DividerMed',
    borderWidth: 1,
    borderStyle: 'solid',
    gap: 1,
    py: 2,
    pl: 3,
    pr: 2,
    borderRadius: 4,
    position: 'relative',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flexGrow: 0,
    minWidth: 0,
    height: 48,
  },
  emptyText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    '&:empty': {
      animation: 'skeleton 1s infinite',
      width: 250,
      borderRadius: 2,
      backgroundColor: 'custom.Skeleton',
      height: '20px',
    },
  },
  removeButton: {
    color: 'text.primary',
  },
};
